import React, { useEffect, useState } from 'react';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { Links } from '../../../application/constants/links.ts';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import {
  RepairsDetailsDto,
  RepairsOrderClient,
  RepairsOrderQuery,
  RepairsQuery,
  UserQuery,
} from 'services/api/api-client.ts';
import styles from './DetailedRepairs.module.scss';
import { formatDate } from '../../../helpers/abas-helper.ts';
import FileIcon from 'assets/icons/file-blue.svg?react';
import clsx from 'clsx';
import { tDynamic } from '../../../helpers/tDynamic.ts';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { defineDeviceImage } from '../warranty-request/components/SerialNumberInfo.tsx';
import GearIcon from 'assets/icons/gear.svg?react';
import DoubleTick from 'assets/icons/double-tick.svg?react';
import ForbiddenErrorPage from '../../unauthorized/errorPages/ForbiddenErrorPage.tsx';
import { AxiosError } from 'axios';
import ErrorPage from '../../unauthorized/errorPages/ErrorPage.tsx';
import {
  costEstimateApprovedStatuses,
  costEstimateNeedsApprovalStatuses,
  Erfasst,
  getStatus,
  statuses,
} from './components/statuses.ts';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import { EstimateModal } from './components/EstimateModal.tsx';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';
import { useLocation, useNavigate } from 'react-router';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { Trans } from 'react-i18next';

export const refetch = 'refetch';
const singleStatus = 1;

export const DetailedRepairs = () => {
  const i18n = useScopedTranslation('Page.DetailedRepairs');
  const i18nRepairs = useScopedTranslation('Page.Repairs');
  const routeParams = Links.Authorized.DetailedRepairs.useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [isCostEstimateModalOpen, setIsCostEstimateModalOpen] = useState(false);
  const [isCostEstimateSubmitting, setIsCostEstimateSubmitting] =
    useState(false);
  const [isOpenFileClicked, setIsOpenFileClicked] = useState(false);
  const [isPDFGenerating, setIsPDFGenerating] = useState(false);
  const [isErrorTooltipVisible, setIsErrorTooltipVisible] = useState(false);

  const setIsClicked = (value: boolean) => {
    setIsOpenFileClicked(value);
  };

  const setIsSubmitting = (value: boolean) => {
    setIsCostEstimateSubmitting(value);
  };
  const userQuery = UserQuery.useGetCurrentUserInfoQuery({
    staleTime: 60 * 1000,
    refetchOnMount: false,
  });
  const isApproveCostEstimateEnabled = Boolean(
    userQuery.data?.features.approveCostEstimateEnabled,
  );
  const isViewCostEstimateEnabled = Boolean(
    userQuery.data?.features.viewCostEstimateEnabled,
  );
  const detailedInfoQuery = RepairsQuery.useGetDetailsByIncomingNumberQuery<
    RepairsDetailsDto,
    AxiosError
  >(
    {
      incomingNumber: routeParams.incomingNumber,
      language: i18n.i18n.language,
    },
    {
      retry: false,
      throwOnError: false,
      staleTime: 30 * 1000,
    },
  );

  const costEstimateQuery = RepairsOrderQuery.useGetSalesViewQuery(
    routeParams.incomingNumber,
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      retry: false,
      enabled: isViewCostEstimateEnabled || isApproveCostEstimateEnabled,
    },
  );

  const costEstimateFile = RepairsOrderQuery.usePrintSalesQuery(
    routeParams.incomingNumber,
    {
      throwOnError: false,
      retry: false,
      enabled: isViewCostEstimateEnabled || isApproveCostEstimateEnabled,
    },
  );

  useEffect(() => {
    if (!detailedInfoQuery.isFetching && location.state === refetch) {
      void navigate(location.pathname, { replace: true, state: null });
    }
  }, [detailedInfoQuery, location.state]);

  const file = costEstimateFile.data;
  const isFileLoading = costEstimateFile.isLoading;

  useEffect(() => {
    if (!isFileLoading && file && isOpenFileClicked && file?.href) {
      window.open(file.href, '_blank');
    }
  }, [isFileLoading, file]);

  const onCostEstimateApprove = async () => {
    await detailedInfoQuery.refetch();
  };

  const generatePDF = async () => {
    try {
      setIsPDFGenerating(true);

      const pdfInfo = await RepairsOrderClient.printRepairOrder([
        routeParams.incomingNumber,
      ]);

      const url = URL.createObjectURL(pdfInfo.data);
      window.open(url, '_blank');
    } catch (e) {
      setIsErrorTooltipVisible(true);
      setTimeout(() => setIsErrorTooltipVisible(false), 4000);
    } finally {
      setIsPDFGenerating(false);
    }
  };

  if (detailedInfoQuery.isError) {
    if (detailedInfoQuery.error?.response?.status === 403) {
      return <ForbiddenErrorPage />;
    }
    return <ErrorPage />;
  }

  if (!detailedInfoQuery.isFetchedAfterMount) {
    return <Loading loading={true} />;
  }

  const data = detailedInfoQuery.data;
  const records = data?.records ?? [];
  const firstRecord = records.at(0);
  const otherRecords = records.slice(1);

  const statusHistory = data?.statusHistory
    ? [...data.statusHistory].reverse()
    : [];
  const firstStatusHistory = statusHistory.at(0);
  const otherStatusHistory = statusHistory.slice(1);

  const status = firstStatusHistory?.status;
  const statusData = status ? getStatus(status) : null;
  const statusDate = firstStatusHistory?.createdAt;
  const deviceName = firstRecord?.name;
  const isDetailsLoading = detailedInfoQuery.isLoading;

  const isCostEstimateNeedsApproval = status
    ? costEstimateNeedsApprovalStatuses.includes(status)
    : false;

  const isCostEstimateApproved = status
    ? costEstimateApprovedStatuses.includes(status)
    : false;

  const onCloseCostEstimateModal = () => {
    setIsCostEstimateModalOpen(false);
  };

  const isWarningMessageNeeded =
    FeatureFlags.isTodayStage() ||
    FeatureFlags.isGreenStage() ||
    FeatureFlags.isCareStage();

  const showApproveCostEstimateButton =
    isCostEstimateNeedsApproval && isApproveCostEstimateEnabled;

  const isMarkAsSentVisible =
    statusHistory.length === singleStatus &&
    statusData?.key === statuses[Erfasst].key;

  return (
    <div data-test-id={'detailed-repairs-container'} className={styles.page}>
      <Breadcrumbs
        pageName={`${i18n.t('request')} #${routeParams.incomingNumber}`}
        children={
          <div className={clsx(styles.rowContainer, styles.columnGap14)}>
            {(isViewCostEstimateEnabled || isApproveCostEstimateEnabled) &&
              // isCostEstimateNeedsApproval is temporary solution, because if isViewCostEstimateEnabled == false and isCostEstimateNeedsApproval == true, then user can see cost estimate modal, but he can't see button to open it
              (isCostEstimateApproved || isCostEstimateNeedsApproval) && (
                <Button
                  title={i18n.t('open_cost_estimate')}
                  color={ButtonColor.Secondary}
                  onClick={() => setIsCostEstimateModalOpen(true)}
                />
              )}
            {FeatureFlags.isPDFGeneratingEnabled() && (
              <Button
                title={i18n.t('open_pdf')}
                color={ButtonColor.Secondary}
                onClick={generatePDF}
              />
            )}
          </div>
        }
      />
      <Loading
        loading={
          detailedInfoQuery.isLoading ||
          (detailedInfoQuery.isFetching && location.state === refetch) ||
          isPDFGenerating
        }
        centerContent={false}
        title={isPDFGenerating ? i18n.t('opening_pdf') : undefined}
        textClassName={styles.loaderText}
      >
        <div className={styles.mainInfoBlock}>
          <div className={clsx(styles.mainInfoCell, styles.relative)}>
            <div className={styles.headerText}>
              {i18n.t('device_and_parts')}
            </div>
            {!isDetailsLoading && (
              <div
                className={clsx(
                  styles.overflow,
                  styles.rowGap12,
                  isWarningMessageNeeded && styles.overflowDev,
                )}
              >
                {firstRecord && (
                  <div
                    className={clsx(styles.rowContainer, styles.columnGap14)}
                  >
                    <div>
                      <div className={styles.deviceIconContainer}>
                        {deviceName &&
                          defineDeviceImage(deviceName, styles.icon, true)}
                      </div>
                    </div>
                    <div
                      className={clsx(
                        styles.columnContainer,
                        styles.rowGap6,
                        styles.minWidth0,
                      )}
                    >
                      <div className={clsx(styles.text, styles.longText)}>
                        {deviceName}
                      </div>
                      <div
                        className={clsx(
                          styles.rowContainer,
                          styles.columnGap20,
                        )}
                      >
                        <div className={styles.rowContainer}>
                          <div
                            className={clsx(
                              styles.greyText,
                              styles.breakSpaces,
                            )}
                          >
                            {i18n.t('sn')}
                          </div>
                          <div
                            data-test-id={'device-sn'}
                            className={styles.text}
                          >
                            {firstRecord.sn}
                          </div>
                        </div>
                        <div className={styles.rowContainer}>
                          <div
                            className={clsx(
                              styles.greyText,
                              styles.breakSpaces,
                            )}
                          >
                            {i18n.t('article')}
                          </div>
                          <div className={styles.text}>
                            {firstRecord.article}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {otherRecords.map((record, index) => {
                  return (
                    <div
                      key={record.name + record.article + record.status}
                      className={clsx(
                        styles.rowContainer,
                        styles.columnGap14,
                        index === otherRecords.length - 1 &&
                          styles.marginBottom24,
                      )}
                    >
                      <div>
                        <div
                          className={clsx(
                            styles.deviceIconContainer,
                            styles.icon,
                          )}
                        >
                          <GearIcon />
                        </div>
                      </div>
                      <div
                        className={clsx(
                          styles.columnContainer,
                          styles.rowGap6,
                          styles.minWidth0,
                        )}
                      >
                        <div className={clsx(styles.text, styles.longText)}>
                          {record.name}
                        </div>
                        <div
                          className={clsx(
                            styles.rowContainer,
                            styles.columnGap20,
                          )}
                        >
                          <div className={styles.rowContainer}>
                            <div
                              className={clsx(
                                styles.greyText,
                                styles.breakSpaces,
                              )}
                            >
                              {i18n.t('article')}
                            </div>
                            <div className={styles.text}>{record.article}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className={styles.fade} />
          </div>
          <div className={clsx(styles.mainInfoCell, styles.relative)}>
            <div className={styles.orderTimeline}>
              <div className={styles.headerText}>
                {i18n.t('order_timeline')}
              </div>
              <div data-test-id={'device-date'} className={styles.greyText}>
                {i18n.t('created_on')} {formatDate(firstRecord?.from ?? '')}
              </div>
            </div>

            {!isDetailsLoading && (
              <div
                className={clsx(
                  styles.overflow,
                  isWarningMessageNeeded && styles.overflowDev,
                )}
              >
                <div
                  className={clsx(
                    styles.rowContainer,
                    styles.columnGap8,
                    styles.relative,
                  )}
                >
                  <div>
                    <div
                      className={clsx(
                        styles.statusCircle,
                        statusData?.className,
                      )}
                    >
                      {statusData ? <statusData.Icon /> : <DoubleTick />}
                    </div>
                  </div>
                  <div
                    className={clsx(styles.columnContainer, styles.minWidth0)}
                  >
                    <div
                      data-test-id={'device-status-collection'}
                      className={clsx(styles.bold, styles.longText)}
                    >
                      <span data-test-id={'first-status-text'}>
                        {statusData
                          ? tDynamic(i18nRepairs, statusData.key)
                          : firstStatusHistory?.status}
                      </span>
                    </div>
                    {statusDate && (
                      <div
                        data-test-id={'first-status-date'}
                        className={styles.dateText}
                      >
                        {formatDate(statusDate)}
                      </div>
                    )}
                  </div>

                  {showApproveCostEstimateButton && (
                    <ActionText
                      text={i18n.t('respond_to_estimate')}
                      testId="respond-to-estimate"
                      Svg={FileIcon}
                      className={styles.respondToCostEstimate}
                      onClickHandler={() => setIsCostEstimateModalOpen(true)}
                    />
                  )}
                </div>

                {FeatureFlags.isMarkAsSentEnabled() && isMarkAsSentVisible && (
                  <div className={styles.markAsSentContainer}>
                    <div
                      className={clsx(
                        styles.text,
                        styles.marginBottom8,
                        styles.fontWeight500,
                      )}
                    >
                      {i18n.t('mark_as_sent_body')}
                    </div>
                    <div
                      className={clsx(styles.rowContainer, styles.markAsSent)}
                    >
                      <DoubleTick />
                      <div>{i18n.t('mark_as_sent')}</div>
                    </div>
                    {FeatureFlags.isPDFGeneratingEnabled() && (
                      <>
                        <div className={styles.markAsSentDivider} />
                        <div
                          className={clsx(styles.greyText, styles.displayRuby)}
                        >
                          <Trans
                            i18nKey={
                              'Page.DetailedRepairs.mark_as_sent_reminder'
                            }
                            components={{
                              a: (
                                <div
                                  onClick={generatePDF}
                                  className={clsx(
                                    styles.actionText,
                                    styles.fontWeight400,
                                  )}
                                />
                              ),
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {otherStatusHistory.map((status, index) => {
                  const statusData = getStatus(status.status);

                  return (
                    <div
                      key={status.status + status.id}
                      className={clsx(
                        styles.relative,
                        index === otherStatusHistory.length - 1 &&
                          styles.marginBottom24,
                      )}
                    >
                      <div
                        className={clsx(
                          styles.divider,
                          showApproveCostEstimateButton &&
                            !index &&
                            styles.longDivider,
                        )}
                      />
                      <div
                        className={clsx(
                          styles.rowContainer,
                          styles.columnGap8,
                          styles.marginTop20,
                          showApproveCostEstimateButton &&
                            !index &&
                            styles.marginTop48,
                        )}
                      >
                        <div>
                          <div className={clsx(styles.statusCircle)}>
                            <DoubleTick />
                          </div>
                        </div>
                        <div
                          className={clsx(
                            styles.columnContainer,
                            styles.minWidth0,
                          )}
                        >
                          <div className={clsx(styles.bold, styles.longText)}>
                            {statusData
                              ? tDynamic(i18nRepairs, statusData.key)
                              : status.status}
                          </div>
                          <div className={styles.dateText}>
                            {formatDate(status.createdAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className={styles.fade} />
          </div>
          <div className={clsx(styles.mainInfoCell, styles.noBorderRight)}>
            <div className={styles.headerText}>
              {i18n.t('delivery_address')}
            </div>
            {!detailedInfoQuery.isLoading && data?.deliveryInfo && (
              <div className={clsx(styles.columnContainer, styles.rowGap20)}>
                <div className={clsx(styles.columnContainer, styles.rowGap4)}>
                  <div className={styles.greyText}>{i18n.t('company')}</div>
                  <div className={styles.text}>
                    {data.deliveryInfo.company || '-'}
                  </div>
                </div>
                <div className={clsx(styles.rowContainer, styles.flexStart)}>
                  <div className={styles.addressBlock}>
                    <div className={styles.greyText}>{i18n.t('dept')}</div>
                    <div className={styles.text}>
                      {data.deliveryInfo.departure || '-'}
                    </div>
                  </div>
                  <div className={styles.addressBlock}>
                    <div className={styles.greyText}>{i18n.t('contact')}</div>
                    <div className={styles.text}>
                      {data.deliveryInfo.contact || '-'}
                    </div>
                  </div>
                </div>
                <div className={styles.addressBlock}>
                  <div className={styles.greyText}>{i18n.t('street')}</div>
                  <div className={styles.text}>
                    {data.deliveryInfo.street || '-'}
                  </div>
                </div>
                <div className={clsx(styles.rowContainer, styles.flexStart)}>
                  <div className={styles.addressBlock}>
                    <div className={styles.greyText}>{i18n.t('city')}</div>
                    <div className={styles.text}>
                      {data.deliveryInfo.city || '-'}
                    </div>
                  </div>
                  <div className={styles.addressBlock}>
                    <div className={styles.greyText}>{i18n.t('country')}</div>
                    <div className={styles.text}>
                      {data.deliveryInfo.country || '-'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {isCostEstimateModalOpen && (
          <EstimateModal
            onClose={onCloseCostEstimateModal}
            isApproveCostEstimateEnabled={isApproveCostEstimateEnabled}
            isCostEstimateApproved={isCostEstimateApproved}
            onCostEstimateApprove={onCostEstimateApprove}
            data={costEstimateQuery.data}
            file={file}
            isDataLoading={costEstimateQuery.isLoading}
            isFileLoading={isFileLoading}
            setIsSubmitting={setIsSubmitting}
            isCostEstimateSubmitting={isCostEstimateSubmitting}
            isOpenFileClicked={isOpenFileClicked}
            setIsClicked={setIsClicked}
          />
        )}
        {isErrorTooltipVisible && (
          <Tooltip
            closeTooltip={() => setIsErrorTooltipVisible(false)}
            bodyContent={<div>{i18n.t('error_popup')}</div>}
            className={styles.tooltip}
            iconClassName={styles.tooltipIcon}
            showCancelButton={true}
            isError={true}
          />
        )}
      </Loading>
    </div>
  );
};
